<script setup lang="ts">
import { computed, ref } from 'vue';
import BottomSheet, { type BottomSheetPosition } from '@/components/layout/BottomSheet.vue';
import SearchBar from '@/components/ui/SearchBar.vue';
import ActionButton from '@/components/ui/ActionButton.vue';
import EmptyState from '@/components/ui/EmptyState.vue';
import SkeletonBlock from '@/components/ui/SkeletonBlock.vue';
import GroupItem from './GroupItem.vue';
import Common from '@/libs/common';
import type { Group } from '@/@types/group';
import type { LatLong } from '@/stores/app';
import { findGroupsAtPosition, rankGroupsByDistance } from './groupBounds';

type SectionKey = 'single' | 'several' | 'nearby' | 'locate' | 'all';

interface GroupSection {
  sectionKey: SectionKey;
  items: Group[];
}

const NEARBY_COUNT = 5;
const SKELETON_ROWS = 6;

const props = defineProps<{
  groups: Group[];
  userPosition?: LatLong;
  hasLoadError?: boolean;
  isLoading?: boolean;
  isLocationDenied?: boolean;
}>();

const emit = defineEmits<{
  'select-group': [group: Group];
  retry: [];
  locate: [];
  'search-focus': [];
  'search-close': [];
  /** Measured height of the collapsed sheet, for the map to fit above it. */
  'sheet-height': [height: number];
}>();

const search = ref('');
const sheetPosition = ref<BottomSheetPosition>('intermediate');

const query = computed<string>(() => Common.normalize(search.value));

const groupSections = computed<GroupSection[]>(() => {
  const q = query.value;

  const filtered = q ? props.groups.filter(g => Common.normalize(g.name).includes(q)) : props.groups;

  if (!props.userPosition) {
    return [
      ...(q ? [] : [{ sectionKey: 'locate' as const, items: [] }]),
      { sectionKey: 'all', items: filtered },
    ];
  }

  const { ranked, unranked } = rankGroupsByDistance(filtered, props.userPosition);

  if (q) {
    return [{ sectionKey: 'all', items: [...ranked, ...unranked] }];
  }

  const candidates = findGroupsAtPosition(filtered, props.userPosition);
  const preselection: GroupSection[] = [];
  if (candidates.length === 1) {
    preselection.push({ sectionKey: 'single', items: candidates });
  } else if (candidates.length > 1) {
    preselection.push({ sectionKey: 'several', items: candidates });
  } else if (ranked.length) {
    preselection.push({ sectionKey: 'nearby', items: ranked.slice(0, NEARBY_COUNT) });
  }

  return [...preselection, { sectionKey: 'all', items: filtered }];
});

// Only while searching: an empty list before `getGroups()` answers is loading, not "no match".
const hasNoResult = computed<boolean>(
  () => !props.isLoading && !!query.value && groupSections.value.every(section => !section.items.length),
);

const sectionTitleKeys: Record<SectionKey, string> = {
  single: 'selectGroup.yourPosition',
  several: 'selectGroup.severalNetworks',
  nearby: 'selectGroup.nearbyNetworkList',
  locate: 'selectGroup.findMyNetwork',
  all: 'selectGroup.networkList',
};

function onSearchFocus() {
  sheetPosition.value = 'full';
  emit('search-focus');
}

async function closeSearch() {
  // Add timeout to avoid cancel click on a group when loosing search focus
  setTimeout(() => {
    sheetPosition.value = 'intermediate';
    emit('search-close');
  }, 200);
}
</script>

<template>
  <BottomSheet
    v-model:position="sheetPosition"
    class="select-group-drawer"
    @update:intermediateHeight="emit('sheet-height', $event)"
  >
    <template #header>
      <h1 class="select-group-drawer__title">
        {{ $t('selectGroup.select') }}
      </h1>
      <div class="select-group-drawer__search-row">
        <SearchBar
          v-model="search"
          :placeholder="$t('selectGroup.searchNetwork')"
          @focus="onSearchFocus()"
          @blur="closeSearch()"
        />
      </div>
    </template>

    <div v-if="hasLoadError" class="select-group-drawer__error">
      <p class="select-group-drawer__error-text">{{ $t('selectGroup.loadError') }}</p>
      <ActionButton
        :label="$t('common.retry')"
        leading-icon="rotate-ccw"
        type="outline"
        @click="emit('retry')"
      />
    </div>

    <div v-else class="select-group-drawer__sections">
      <template v-for="section in groupSections" :key="section.sectionKey">
        <section v-if="section.sectionKey !== 'all' || !isLoading" class="select-group-drawer__section">
          <!-- Searching flattens the list: the results are no longer "near you" vs "all". -->
          <h2 v-if="!query" class="select-group-drawer__section-title">
            {{ $t(sectionTitleKeys[section.sectionKey]) }}
          </h2>

          <EmptyState
            v-if="section.sectionKey === 'locate'"
            class="select-group-drawer__card"
            icon="map-pin"
            :title="$t('selectGroup.enableLocation')"
            :description="
              $t(isLocationDenied ? 'selectGroup.locationDisabled' : 'selectGroup.enableLocationHint')
            "
          >
            <template v-if="!isLocationDenied" #actions>
              <ActionButton
                :label="$t('selectGroup.locateMe')"
                type="primary"
                class="select-group-drawer__locate"
                @click="emit('locate')"
              />
            </template>
          </EmptyState>

          <div v-else-if="section.sectionKey === 'single'" class="select-group-drawer__card">
            <GroupItem
              v-for="group in section.items"
              :key="group.group_id"
              :group="group"
              @click="emit('select-group', group)"
            />
            <p class="select-group-drawer__hint">{{ $t('selectGroup.availableAtPosition') }}</p>
            <ActionButton
              :label="$t('selectGroup.chooseNetwork')"
              type="primary"
              class="select-group-drawer__choose"
              @click="emit('select-group', section.items[0])"
            />
          </div>

          <template v-else>
            <p v-if="section.sectionKey === 'several'" class="select-group-drawer__hint">
              {{ $t('selectGroup.severalNetworksHint', { count: section.items.length }) }}
            </p>
            <p v-else-if="section.sectionKey === 'nearby'" class="select-group-drawer__hint">
              {{ $t('selectGroup.noNetworkHere') }}
            </p>
            <div class="select-group-drawer__rows">
              <GroupItem
                v-for="group in section.items"
                :key="group.group_id"
                :group="group"
                @click="emit('select-group', group)"
              />
            </div>
          </template>
        </section>
      </template>

      <section v-if="isLoading" class="select-group-drawer__section">
        <div class="select-group-drawer__rows">
          <SkeletonBlock
            v-for="n in SKELETON_ROWS"
            :key="n"
            class="select-group-drawer__skeleton"
            shape="rectangle"
          />
        </div>
      </section>

      <p v-if="hasNoResult" class="select-group-drawer__empty">{{ $t('search.noResults') }}</p>
    </div>
  </BottomSheet>
</template>

<style lang="scss">
.select-group-drawer {
  // The header keeps the sheet's white, the list sits on the grey behind the rows.
  .bottom-sheet__content {
    background-color: $color-neutral-lower;
  }

  &__title {
    color: $color-neutral-highest;
    font: $heading-h1;
    letter-spacing: $heading-h1-spacing;
    margin: 0;
    padding: $spacing-5 $spacing-4 $spacing-3;
  }

  &__search-row {
    border-bottom: 1px solid $color-border-medium;
    padding: $spacing-2 $spacing-4 $spacing-5;

    .searchbar {
      margin-bottom: 0;
    }
  }

  &__sections {
    // The WebView keeps its size under the keyboard: pad by its height so the
    // last rows can still be scrolled into view (see capacitor.ts).
    padding-bottom: calc(env(safe-area-inset-bottom, 0px) + var(--keyboard-height, 0px));
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: $spacing-3;
    padding: $spacing-4 $spacing-4 $spacing-5;
  }

  &__section-title {
    color: $color-neutral-highest;
    font: $heading-h4;
    letter-spacing: $heading-h4-spacing;
    margin: 0;
  }

  &__card {
    background-color: $color-white;
    border-radius: $radius-2xl;
    display: flex;
    flex-direction: column;
    gap: $spacing-3;
    padding: $spacing-4;
  }

  &__hint {
    color: $color-neutral-higher;
    font: $text-small;
    letter-spacing: $text-small-spacing;
    margin: 0;
  }

  &__skeleton {
    height: 60px;
  }

  &__rows {
    display: flex;
    flex-direction: column;
    gap: $spacing-2;
  }

  &__error {
    align-items: center;
    color: $color-neutral-higher;
    display: flex;
    flex-direction: column;
    font: $text-small;
    gap: $spacing-4;
    letter-spacing: $text-small-spacing;
    padding: $spacing-5 $spacing-4;
    text-align: center;
  }

  &__error-text {
    margin: 0;
  }

  &__empty {
    color: $color-neutral-higher;
    font: $text-small;
    letter-spacing: $text-small-spacing;
    margin: 0;
    padding: 0 $spacing-4 $spacing-5;
  }
}
</style>
